import React, { useState, useEffect } from 'react';
import { X, Receipt, AlertCircle } from 'lucide-react';
import { useCreateAncillaryFee, useUpdateAncillaryFee } from '../../hooks/useAncillaryFees';
import type { AncillaryFee, CreateAncillaryFeeData, UpdateAncillaryFeeData } from '@/types/tuition.types';

interface Props {
  fee: AncillaryFee | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AncillaryFeeFormModal: React.FC<Props> = ({ fee, onClose, onSuccess }) => {
  const isEditing = !!fee;

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    description: '',
    isRecurring: false,
  });

  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateAncillaryFee();
  const updateMutation = useUpdateAncillaryFee();

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Pre-fill form when editing
  useEffect(() => {
    if (fee) {
      setFormData({
        name: fee.name,
        amount: String(fee.amount),
        description: fee.description || '',
        isRecurring: fee.isRecurring,
      });
    }
  }, [fee]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Le nom est requis');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) {
      setError('Le montant doit être un nombre positif');
      return;
    }

    try {
      if (isEditing && fee) {
        const updateData: UpdateAncillaryFeeData = {
          name: formData.name.trim(),
          amount,
          description: formData.description.trim() || undefined,
          isRecurring: formData.isRecurring,
        };
        await updateMutation.mutateAsync({ id: fee.id, data: updateData });
      } else {
        const createData: CreateAncillaryFeeData = {
          name: formData.name.trim(),
          amount,
          description: formData.description.trim() || undefined,
          isRecurring: formData.isRecurring,
        };
        await createMutation.mutateAsync(createData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Modifier le frais annexe' : 'Nouveau frais annexe'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Modifiez les informations du frais' : 'Créez un nouveau frais annexe'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du frais <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Attestation de scolarité"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant (MAD) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description optionnelle du frais..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Is Recurring */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isRecurring"
              checked={formData.isRecurring}
              onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
              className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <label htmlFor="isRecurring" className="block text-sm font-medium text-gray-900 cursor-pointer">
                Frais récurrent (annuel)
              </label>
              <p className="text-sm text-gray-500 mt-0.5">
                {formData.isRecurring
                  ? "Ce frais sera automatiquement appliqué chaque année académique aux étudiants concernés."
                  : "Ce frais est ponctuel et ne sera appliqué qu'une seule fois aux étudiants affectés."}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : isEditing ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AncillaryFeeFormModal;
